import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  Sparkles, 
  ArrowRight, 
  Clock, 
  Search
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { insightsTr } from '../data/insights';
import { insightsEn } from '../data/insights.en';

const Insights: React.FC = () => {
  const { language } = useLanguage();
  const isEn = language === 'EN';

  const allInsights = isEn ? insightsEn : insightsTr;

  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const canonicalUrl = isEn
    ? "https://www.we24agency.com/en/insights"
    : "https://www.we24agency.com/icgoruler";
  const alternateTr = "https://www.we24agency.com/icgoruler";
  const alternateEn = "https://www.we24agency.com/en/insights";

  // Category definitions
  const categories = useMemo(() => {
    if (isEn) {
      return [
        { id: 'all', label: 'All Insights' },
        { id: 'Strategy', label: 'Strategy & IPO' },
        { id: 'Live Broadcast', label: 'Live Broadcast' },
        { id: 'Production', label: 'Industrial & Craft' },
        { id: 'Guides', label: 'Guides & Briefs' }
      ];
    }
    return [
      { id: 'all', label: 'Tüm İçgörüler' },
      { id: 'Strateji', label: 'Strateji & Halka Arz' },
      { id: 'Canlı Yayın', label: 'Canlı Yayın & Reji' },
      { id: 'Prodüksiyon', label: 'Sanayi & Prodüksiyon' },
      { id: 'Rehberler', label: 'Rehber & Şablonlar' }
    ];
  }, [isEn]);

  const filteredInsights = useMemo(() => {
    return allInsights.filter((item) => {
      const matchCategory = selectedCategory === 'all' || item.category === selectedCategory;
      const matchSearch = searchQuery.trim() === '' || 
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.tags.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchCategory && matchSearch;
    });
  }, [allInsights, selectedCategory, searchQuery]);

  const featuredInsight = useMemo(() => {
    return allInsights.find(item => item.featured) || allInsights[0];
  }, [allInsights]);

  return (
    <div className="bg-[#030811] text-foreground min-h-screen">
      <Helmet>
        <title>{isEn ? "Insights & Production Playbook - WE24 Agency" : "İçgörüler & Prodüksiyon Rehberi - WE24 Agency"}</title>
        <meta 
          name="description" 
          content={isEn 
            ? "Strategic perspectives, zero-latency broadcast architecture, industrial cinematography craft, and video production playbooks curated by WE24 Agency directors."
            : "WE24 Agency yönetmenleri ve strateji ekibinden halka arz video stratejileri, sıfır gecikmeli canlı yayın rejisi, endüstriyel sinematografi ve brief rehberleri."
          } 
        />
        <meta name="keywords" content="Video Prodüksiyon Rehberi, Canlı Yayın Reji Mimarisi, Halka Arz Reklam Filmi, Fabrika Tanıtım Filmi, Brief Şablonu, WE24 Insights, Prodüksiyon İçgörüleri" />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" hrefLang="tr" href={alternateTr} />
        <link rel="alternate" hrefLang="en" href={alternateEn} />
        <link rel="alternate" hrefLang="x-default" href={alternateTr} />

        {/* OpenGraph */}
        <meta property="og:title" content={isEn ? "Insights & Production Playbook - WE24 Agency" : "İçgörüler & Prodüksiyon Rehberi - WE24 Agency"} />
        <meta property="og:description" content={isEn ? "Strategic insights on cinematic production and broadcast technology." : "Sinematik prodüksiyon ve canlı yayın teknolojisi üzerine stratejik içgörüler."} />
        <meta property="og:image" content="https://www.we24agency.com/project-covers/kristal-piksel-2025-canli-yayin.webp" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="WE24 Agency" />
        <meta property="og:locale" content={isEn ? "en_US" : "tr_TR"} />

        {/* Structured Data JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": isEn ? "Insights & Production Playbook" : "İçgörüler & Prodüksiyon Rehberi",
            "description": isEn 
              ? "Strategic insights and engineering guides on commercial video production and live broadcasting."
              : "Sinematik video prodüksiyonu ve canlı yayın mühendisliği üzerine stratejik rehberler ve vaka analizleri.",
            "url": canonicalUrl,
            "publisher": {
              "@type": "Organization",
              "name": "WE24 Agency",
              "url": "https://www.we24agency.com"
            }
          })}
        </script>
      </Helmet>

      {/* Hero Header */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 border-b border-white/[0.08] overflow-hidden bg-gradient-to-b from-[#061524] via-[#030811] to-[#030811]">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[350px] bg-[#ff7a63]/10 blur-[130px] rounded-full pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#ff7a63]/10 border border-[#ff7a63]/25 text-[#ff7a63] text-xs font-bold uppercase tracking-wider mb-6">
            <Sparkles className="w-3.5 h-3.5" />
            {isEn ? "WE24 Strategic Insights & Production Playbook" : "WE24 Stratejik İçgörüler & Prodüksiyon Rehberi"}
          </div>

          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight font-display max-w-4xl mx-auto leading-tight">
            {isEn ? (
              <>
                Cinema, Live Broadcasting & <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-zinc-200 to-[#ff7a63]">Strategy</span> Perspectives
              </>
            ) : (
              <>
                Sinema, Canlı Yayın ve <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-zinc-200 to-[#ff7a63]">Strateji</span>ye Dair Odak İçgörüler
              </>
            )}
          </h1>

          <p className="text-base sm:text-lg text-zinc-300 max-w-2xl mx-auto mt-6 leading-relaxed">
            {isEn
              ? "Practical guides, technical broadcast architectures, and commercial production playbooks crafted for decision-makers and brand leaders."
              : "Pazarlama liderleri ve karar vericiler için hazırladığımız halka arz reklam stratejileri, sıfır gecikmeli canlı yayın mimarisi ve prodüksiyon kılavuzları."
            }
          </p>

          {/* Search bar */}
          <div className="max-w-md mx-auto mt-8 relative">
            <Search className="w-4 h-4 text-zinc-400 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={isEn ? "Search insights, keywords or topics..." : "İçgörülerde, konularda veya etiketlerde ara..."}
              className="w-full bg-zinc-900/80 border border-white/10 rounded-full pl-11 pr-5 py-3 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-[#ff7a63] transition-colors"
            />
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <section className="py-16 max-w-7xl mx-auto px-6">
        {/* Category Filters */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-12">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-5 py-2.5 rounded-full text-xs font-bold transition-all ${
                selectedCategory === cat.id
                  ? 'bg-white text-zinc-950 shadow-lg scale-105'
                  : 'bg-zinc-900/60 border border-white/10 text-zinc-300 hover:text-white hover:border-white/20'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Featured Article Card (Shown when on 'all' and no search query) */}
        {selectedCategory === 'all' && !searchQuery && featuredInsight && (
          <div className="mb-16">
            <Link
              to={isEn ? `/en/insights/${featuredInsight.slug}` : `/icgoruler/${featuredInsight.slug}`}
              className="group block bg-gradient-to-br from-zinc-900/70 via-zinc-900/40 to-zinc-950 border border-white/10 hover:border-[#ff7a63]/40 rounded-3xl overflow-hidden transition-all shadow-2xl"
            >
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 p-6 sm:p-8 items-center">
                <div className="lg:col-span-6 relative aspect-video rounded-2xl overflow-hidden bg-zinc-950 border border-white/5">
                  <img
                    src={featuredInsight.coverImage}
                    alt={featuredInsight.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 rounded-full bg-[#ff7a63] text-zinc-950 text-xs font-black uppercase tracking-wider shadow-lg">
                      {isEn ? "Featured Insight" : "Öne Çıkan İçgörü"}
                    </span>
                  </div>
                </div>
                <div className="lg:col-span-6 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-3 text-xs text-zinc-400 mb-3">
                      <span className="text-[#ff7a63] font-bold uppercase tracking-wider">{featuredInsight.category}</span>
                      <span>•</span>
                      <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{featuredInsight.readTime}</span>
                      <span>•</span>
                      <span>{featuredInsight.publishDate}</span>
                    </div>
                    <h2 className="text-xl sm:text-3xl font-bold text-white group-hover:text-[#ff7a63] transition-colors font-display leading-snug mb-4">
                      {featuredInsight.title}
                    </h2>
                    <p className="text-sm sm:text-base text-zinc-300 leading-relaxed mb-6">
                      {featuredInsight.excerpt}
                    </p>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-white/10">
                    <div className="text-xs">
                      <span className="text-white font-bold block">{featuredInsight.author.name}</span>
                      <span className="text-zinc-400 text-[11px]">{featuredInsight.author.role}</span>
                    </div>
                    <span className="inline-flex items-center gap-2 text-xs font-bold text-[#ff7a63] uppercase tracking-wider group-hover:translate-x-1 transition-transform">
                      {isEn ? "Read Full Insight" : "İçgörüyü Oku"}
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        )}

        {/* Articles Grid */}
        {filteredInsights.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredInsights.map((insight) => (
              <Link
                key={insight.id}
                to={isEn ? `/en/insights/${insight.slug}` : `/icgoruler/${insight.slug}`}
                className="group flex flex-col justify-between bg-zinc-900/40 border border-white/10 hover:border-[#ff7a63]/40 rounded-3xl p-6 transition-all hover:-translate-y-1 shadow-xl"
              >
                <div>
                  <div className="aspect-video w-full rounded-2xl overflow-hidden bg-zinc-950 mb-5 border border-white/5 relative">
                    <img
                      src={insight.coverImage}
                      alt={insight.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-3 py-1 rounded-full bg-zinc-950/80 backdrop-blur-md text-[#ff7a63] text-[11px] font-bold uppercase tracking-wider border border-white/10">
                        {insight.category}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-xs text-zinc-400 mb-3">
                    <Clock className="w-3.5 h-3.5 text-zinc-500" />
                    <span>{insight.readTime}</span>
                    <span>•</span>
                    <span>{insight.publishDate}</span>
                  </div>

                  <h3 className="text-lg font-bold text-white group-hover:text-[#ff7a63] transition-colors leading-snug mb-3 line-clamp-2">
                    {insight.title}
                  </h3>

                  <p className="text-xs text-zinc-400 leading-relaxed line-clamp-3 mb-6">
                    {insight.excerpt}
                  </p>
                </div>

                <div className="pt-4 border-t border-white/10 flex items-center justify-between">
                  <span className="text-[11px] text-zinc-400">{insight.author.name}</span>
                  <span className="text-xs font-bold text-[#ff7a63] inline-flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                    {isEn ? "Read" : "İncele"}
                    <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-zinc-900/30 rounded-3xl border border-white/10">
            <p className="text-zinc-400 text-sm">
              {isEn ? "No insights found matching your query." : "Aradığınız kriterlere uygun içerik bulunamadı."}
            </p>
          </div>
        )}
      </section>

      {/* CTA Box */}
      <section className="py-20 border-t border-white/[0.08] bg-gradient-to-b from-transparent to-[#05131f]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white font-display">
            {isEn ? "Need Strategic Guidance on Your Next Production?" : "Yeni Projeniz İçin Stratejik Danışmanlığa mı İhtiyacınız Var?"}
          </h2>
          <p className="text-sm sm:text-base text-zinc-300 mt-3 max-w-xl mx-auto leading-relaxed">
            {isEn
              ? "Let's align your brand goals, production logistics, and creative vision in a collaborative discovery workshop."
              : "Markanızın hedeflerini, yayın mecralarını ve prodüksiyon planını birlikte kurgulayalım."
            }
          </p>
          <div className="mt-8">
            <Link
              to={isEn ? "/en/contact" : "/iletisim"}
              className="px-8 py-4 bg-white text-zinc-950 hover:bg-zinc-200 text-xs font-extrabold uppercase tracking-wider rounded-full transition-all shadow-xl hover:scale-105 inline-flex items-center gap-2"
            >
              {isEn ? "Schedule a Discovery Meeting" : "Proje Toplantısı Planlayın"}
              <ArrowRight className="w-4 h-4 text-[#ff7a63]" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Insights;
